<template>
  <div class="px-4 py-4 mb-14">
    <div class="mb-4">
      <h2 class="text-sm font-semibold text-neutral-800 mb-1">Meeting Details</h2>
    </div>
    
    <div class="mb-4">
      <label class="block text-xs font-medium text-neutral-600 mb-1">
        Was würdest du gerne besprechen?
      </label>
      <textarea 
        v-model="localFormData.message"
        rows="3"
        placeholder="Teile uns mit, worüber du in unserem Gespräch sprechen möchtest..."
        class="form-textarea"
        :style="{
          width: '100%',
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          padding: '0.5rem 0.75rem',
          borderRadius: '0.375rem',
          fontSize: '0.75rem',
          color: '#374151',
          transition: 'all 0.2s ease',
          resize: 'none'
        }"
        @focus="handleTextareaFocus"
        @blur="handleTextareaBlur"
        @mouseover="handleTextareaHover"
        @mouseout="handleTextareaMouseOut"
      ></textarea>
      <p class="text-xs text-neutral-400 mt-1">Optional – Das hilft uns, uns auf das Gespräch vorzubereiten</p>
    </div>
    
    <div class="mb-6">
      <div 
        class="reminder-box"
        :style="{
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '0.375rem',
          padding: '0.75rem'
        }"
      >
        <label class="flex items-start">
          <input 
            type="checkbox" 
            v-model="localFormData.sendReminder"
            class="custom-checkbox"
            :style="{
              height: '1rem',
              width: '1rem',
              borderRadius: '0.25rem',
              border: '1px solid #d1d5db',
              marginTop: '0.125rem',
              flexShrink: '0',
              transition: 'all 0.2s ease',
              ...getCheckboxStyle()
            }"
            @focus="handleCheckboxFocus"
            @blur="handleCheckboxBlur"
          />
          <div class="ml-3">
            <span class="text-xs font-medium text-neutral-800">Email Erinnerung</span>
            <p class="text-xs text-neutral-500 mt-0.5">Wir senden dir 1 Stunde vor dem Gespräch eine Erinnerung</p>
          </div>
        </label>
      </div>
    </div>

    <!-- Navigation -->
    <div class="flex justify-between">
      <button 
        @click="prevStep"
        class="back-button"
        :style="{
          padding: '0.6rem 1rem',
          fontSize: '0.75rem',
          fontWeight: '500',
          borderRadius: '0.375rem',
          border: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          color: '#374151',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }"
        @mouseover="handleBackButtonHover"
        @mouseout="handleBackButtonMouseOut"
      >
        Zurück
      </button>
      
      <button 
        @click="nextStep"
        class="submit-button"
        :style="{
          padding: '0.6rem 1rem',
          fontSize: '0.75rem',
          fontWeight: '500',
          borderRadius: '0.375rem',
          border: 'none',
          cursor: isSubmitting ? 'not-allowed' : 'pointer',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          alignItems: 'center',
          ...getSubmitButtonStyle()
        }"
        :disabled="isSubmitting"
        :class="{ 'opacity-75 cursor-not-allowed': isSubmitting }"
        @mouseover="handleSubmitButtonHover"
        @mouseout="handleSubmitButtonMouseOut"
      >
        <svg 
          v-if="isSubmitting" 
          class="animate-spin h-3 w-3 mr-1.5" 
          xmlns="http://www.w3.org/2000/svg" 
          fill="none" 
          viewBox="0 0 24 24"
        >
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        {{ isSubmitting ? 'Wird gebucht...' : 'Termin buchen' }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MeetingStep3',
  props: {
    formData: {
      type: Object,
      required: true
    },
    styles: {
      type: Object,
      default: () => ({
        bubbleColor: '#3b82f6',
        textColor: '#ffffff'
      })
    }
  },
  data() {
    return {
      localFormData: { ...this.formData },
      isSubmitting: false
    };
  },
  watch: {
    localFormData: {
      deep: true,
      handler(newValue) {
        console.log('MeetingStep3 form data updated:', newValue);
        this.$emit('update:form-data', newValue);
      }
    }
  },
  mounted() {
    console.log('MeetingStep3 mounted with formData:', this.formData);
    console.log('MeetingStep3 mounted with styles:', this.styles);
    console.log('Initial localFormData:', this.localFormData);
  },
  methods: {
    // Handle textarea focus/hover for Shadow DOM
    handleTextareaFocus(event) {
      event.target.style.borderColor = '#d1d5db';
      event.target.style.backgroundColor = 'white';
      event.target.style.boxShadow = '0 0 0 1px #d1d5db';
    },
    
    handleTextareaBlur(event) {
      event.target.style.borderColor = '#e5e7eb';
      event.target.style.backgroundColor = '#f9fafb';
      event.target.style.boxShadow = 'none';
    },
    
    handleTextareaHover(event) {
      if (document.activeElement !== event.target) {
        event.target.style.borderColor = '#d1d5db';
        event.target.style.backgroundColor = 'white';
      }
    },
    
    handleTextareaMouseOut(event) {
      if (document.activeElement !== event.target) {
        event.target.style.borderColor = '#e5e7eb';
        event.target.style.backgroundColor = '#f9fafb';
      }
    },

    // Handle checkbox focus for Shadow DOM
    handleCheckboxFocus(event) {
      event.target.style.boxShadow = '0 0 0 1px #d1d5db';
    },
    
    handleCheckboxBlur(event) {
      event.target.style.boxShadow = 'none';
    },

    // Handle back button hover for Shadow DOM
    handleBackButtonHover(event) {
      event.target.style.backgroundColor = 'white';
      event.target.style.borderColor = '#d1d5db';
    },
    
    handleBackButtonMouseOut(event) {
      event.target.style.backgroundColor = '#f9fafb';
      event.target.style.borderColor = '#e5e7eb';
    },

    // Handle submit button hover for Shadow DOM
    handleSubmitButtonHover(event) {
      if (!this.isSubmitting) {
        event.target.style.transform = 'translateY(-1px)';
        event.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
      }
    },
    
    handleSubmitButtonMouseOut(event) {
      if (!this.isSubmitting) {
        event.target.style.transform = 'none';
        event.target.style.boxShadow = 'none';
      }
    },

    async nextStep() {
      console.log('Submit clicked with final data:', this.localFormData);
      
      // Show loading state
      this.isSubmitting = true;
      
      try {
        // Simulate submission delay
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        console.log('Emitting next-step to complete submission');
        this.$emit('next-step');
      } catch (error) {
        console.error('Fehler beim Erstellen des Termins:', error);
      } finally {
        this.isSubmitting = false;
      }
    },
    
    prevStep() {
      console.log('Previous step clicked');
      this.$emit('prev-step');
    },
    
    // Get dynamic styling for checkbox
    getCheckboxStyle() {
      const bubbleColor = this.styles.bubbleColor || '#3b82f6';
      
      return {
        'accent-color': bubbleColor
      };
    },
    
    // Get dynamic styling for submit button
    getSubmitButtonStyle() {
      const bubbleColor = this.styles.bubbleColor || '#3b82f6';
      const textColor = this.styles.textColor || '#ffffff';
      
      return {
        backgroundColor: bubbleColor,
        color: textColor
      };
    }
  }
};
</script>

<style scoped>
/* Minimal styles - placeholder styling that can't be done inline */
.form-textarea::placeholder {
  color: #9ca3af;
  font-size: 0.75rem;
}
</style>